/**
 * Billing ML Monitoring (Items 7-8)
 *
 * Tracks the 4 ML modules: denial_classifier, autocode_calibrator,
 * doc_completeness_classifier, braided_ranker.
 * For each: shows latest model artifact, last training run status, metrics,
 * and a "Train now" button (Celery enqueue).
 *
 * Until enough labeled data accumulates each module returns
 * 'insufficient_data' — that's expected and shown explicitly.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getWeaveinApiUrl } from '@/src/config';
import CapabilityGate from '@/components/common/CapabilityGate';
import { PageHeader, PageContainer } from '@/components/weavein';
import { Loader2, RefreshCw, CheckCircle, AlertTriangle, Clock, Brain, Sparkles, ArrowRight } from 'lucide-react';

interface RunRow {
  id: number;
  module: string;
  status: string;
  rows_seen: number | null;
  rows_trained_on: number | null;
  metrics: any;
  error_message: string | null;
  started_at: string | null;
  completed_at: string | null;
  model_id: number | null;
}

interface ModelRow {
  id: number;
  module: string;
  version: string;
  algorithm: string;
  training_rows: number | null;
  metrics: any;
  active: boolean;
  trained_at: string;
}

interface SyntheticPrior {
  id: number;
  module: string;
  version: string;
  training_rows: number | null;
  trained_at: string | null;
  active: boolean;
  data_source: string | null;
  metrics: any;
}

interface SyntheticSummary {
  counts: {
    synth_members?: number;
    synth_claims?: number;
    synth_lines?: number;
    synth_denial_labels?: number;
    synth_activities?: number;
  };
  priors: SyntheticPrior[];
  latest_drift: { id: number; started_at: string; drift_metrics: any } | null;
}

interface ServingEntry {
  module: string;
  serving: boolean;
  serving_source: 'production' | 'synthetic_fallback' | 'rule_only';
  model_id: number | null;
  version?: string;
  metrics?: any;
  trained_at?: string;
}
interface ServingStatus {
  denial_classifier?: ServingEntry;
  braided_ranker?: ServingEntry;
}

const MODULES = [
  {
    key: 'denial_classifier',
    label: 'Denial-prediction classifier',
    description: 'Per-line denial probability across 30+ features. Replaces rule scorer once F1 > 0.75.',
  },
  {
    key: 'autocode_calibrator',
    label: 'Auto-coding calibrator',
    description: 'Isotonic-regression mapping of raw Claude confidence → actual acceptance probability.',
  },
  {
    key: 'doc_completeness_classifier',
    label: 'Doc-completeness fine-tune',
    description: 'Predicts probability the note will survive MCP audit (CARC 197).',
  },
  {
    key: 'braided_ranker',
    label: 'Braided revenue ranker',
    description: 'Co-occurrence-based ranker for missed co-billing opportunities per member.',
  },
];

function authHeaders() {
  const token = localStorage.getItem('auth_token');
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
}

export default function BillingMlPage() {
  const organizationId = Number(localStorage.getItem('active_cbo_id')) || null;
  const [models, setModels] = useState<ModelRow[]>([]);
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [synthSummary, setSynthSummary] = useState<SyntheticSummary | null>(null);
  const [servingStatus, setServingStatus] = useState<ServingStatus | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function load() {
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
    try {
      const [m, r, s, ss] = await Promise.all([
        fetch(getWeaveinApiUrl('/api/admin/billing-ml/models'), {
          credentials: 'include',
          headers: authHeaders(),
        }),
        fetch(getWeaveinApiUrl('/api/admin/billing-ml/runs?limit=20'), {
          credentials: 'include',
          headers: authHeaders(),
        }),
        fetch(getWeaveinApiUrl('/api/admin/synthetic-pretrain/summary'), {
          credentials: 'include',
          headers: authHeaders(),
        }),
        fetch(getWeaveinApiUrl('/api/admin/billing-ml/serving-status'), {
          credentials: 'include',
          headers: authHeaders(),
        }),
      ]);
      if (m.ok) setModels(await m.json());
      if (r.ok) setRuns(await r.json());
      if (s.ok) setSynthSummary(await s.json());
      if (ss.ok) setServingStatus(await ss.json());
    } catch (e: any) {
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return;
      setError(e.message || String(e));
    }
  }

  useEffect(() => {
    void load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);

  async function train(moduleKey: string) {
    setBusy(moduleKey);
    setError(null);
    try {
      const r = await fetch(getWeaveinApiUrl(`/api/admin/billing-ml/train/${moduleKey}`), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      // Give the worker a moment, then refresh
      setTimeout(load, 1500);
    } catch (e: any) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  }

  function fmt(d: string | null | undefined) {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString();
    } catch {
      return String(d);
    }
  }

  function statusPill(status: string) {
    const map: Record<string, { cls: string; Icon: any }> = {
      success: { cls: 'bg-emerald-50 text-emerald-700', Icon: CheckCircle },
      running: { cls: 'bg-blue-50 text-blue-700', Icon: Loader2 },
      insufficient_data: { cls: 'bg-amber-50 text-amber-700', Icon: Clock },
      failed: { cls: 'bg-red-50 text-red-700', Icon: AlertTriangle },
    };
    const m = map[status] || { cls: 'bg-paper-2 text-ink-700', Icon: AlertTriangle };
    const Icon = m.Icon;
    return (
      <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium ${m.cls}`}>
        <Icon className={`h-3 w-3 ${status === 'running' ? 'animate-spin' : ''}`} />
        {status.replace(/_/g, ' ')}
      </span>
    );
  }

  return (
    <CapabilityGate
      organizationId={organizationId}
      capability="billing"
      behavior="show-upgrade"
      upgradeMessage="Billing capability is not enabled for your organization."
    >
      <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Admin · Billing"
        title="Billing ML"
        description="Training status and serving models for the four billing ML modules"
      />
      <PageContainer>
        <div className="rounded-lg border border-v2border-muted bg-paper-2 p-3 text-sm text-ink-500">
          <Brain className="mr-1.5 inline h-4 w-4 text-v2accent" />
          <span className="font-medium text-ink">Billing ML models.</span> Trained on your
          organization's ERAs + Phase 8 denial audit set + Module 5 public reference data. Each
          module returns <code className="text-[12px]">insufficient_data</code> until its minimum
          row threshold is met — that's expected and not an error. Weekly retraining runs
          automatically; trigger manually here when needed.
        </div>

        {/* ── Synthetic priors panel ─────────────────────────── */}
        <SyntheticPriorsPanel summary={synthSummary} />

        {error && <p className="text-sm text-red-600">{error}</p>}

        <section className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {MODULES.map((mod) => {
            const activeModel = models.find((m) => m.module === mod.key && m.active);
            const lastRun = runs.find((r) => r.module === mod.key);
            const serving = (servingStatus as any)?.[mod.key] as ServingEntry | undefined;
            return (
              <div key={mod.key} className="rounded-lg border border-v2border bg-surface p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-sm font-semibold text-ink">{mod.label}</h3>
                    <p className="mt-0.5 text-xs text-ink-500">{mod.description}</p>
                  </div>
                  <button
                    onClick={() => train(mod.key)}
                    disabled={busy === mod.key}
                    className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-paper px-2 py-1 text-xs font-medium text-ink hover:bg-paper-2 disabled:opacity-50"
                  >
                    {busy === mod.key ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                    Train
                  </button>
                </div>

                {serving && <ServingBadge entry={serving} />}

                <dl className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <Dl label="Last run">{lastRun ? statusPill(lastRun.status) : <span className="text-ink-400">never</span>}</Dl>
                  <Dl label="Last ran at">{fmt(lastRun?.completed_at || lastRun?.started_at)}</Dl>
                  <Dl label="Active model">
                    {activeModel ? (
                      <code className="text-[11px]">{activeModel.version}</code>
                    ) : (
                      <span className="text-ink-400">none</span>
                    )}
                  </Dl>
                  <Dl label="Training rows">
                    {activeModel?.training_rows?.toLocaleString() ?? '—'}
                  </Dl>
                </dl>

                {activeModel?.metrics && Object.keys(activeModel.metrics).length > 0 && (
                  <div className="mt-3 rounded-md border border-v2border-muted bg-paper-2 p-2 text-xs">
                    <div className="mb-1 text-[10px] uppercase tracking-wide text-ink-400">
                      Holdout metrics
                    </div>
                    <code className="block whitespace-pre-wrap text-[11px] text-ink-500">
                      {JSON.stringify(activeModel.metrics, null, 2)}
                    </code>
                  </div>
                )}

                {lastRun?.error_message && (
                  <p className="mt-2 text-xs text-red-600">{lastRun.error_message}</p>
                )}
              </div>
            );
          })}
        </section>

        <section className="rounded-lg border border-v2border bg-surface p-4">
          <h2 className="mb-3 text-sm font-semibold text-ink">Recent training runs</h2>
          <div className="overflow-hidden rounded-md border border-v2border-muted">
            <table className="w-full text-sm">
              <thead className="bg-paper-2 text-xs uppercase tracking-wide text-ink-500">
                <tr>
                  <th className="px-3 py-2 text-left">Module</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-right">Rows seen</th>
                  <th className="px-3 py-2 text-right">Rows trained</th>
                  <th className="px-3 py-2 text-left">Started</th>
                  <th className="px-3 py-2 text-left">Completed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-v2border-muted">
                {runs.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-sm text-ink-500">
                      No runs yet. Click <b>Train</b> on any module above.
                    </td>
                  </tr>
                )}
                {runs.map((r) => (
                  <tr key={r.id} className="hover:bg-paper-2/50">
                    <td className="px-3 py-2 text-ink">{r.module}</td>
                    <td className="px-3 py-2">{statusPill(r.status)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.rows_seen?.toLocaleString() ?? '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{r.rows_trained_on?.toLocaleString() ?? '—'}</td>
                    <td className="px-3 py-2 text-xs text-ink-500">{fmt(r.started_at)}</td>
                    <td className="px-3 py-2 text-xs text-ink-500">{fmt(r.completed_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </PageContainer>
      </div>
    </CapabilityGate>
  );
}

function ServingBadge({ entry }: { entry: ServingEntry }) {
  // Three states the badge can land in. Color and copy are derived from
  // the backend's serving_source field — see denial_classifier.predict()
  // and /api/admin/billing-ml/serving-status.
  if (entry.serving_source === 'production') {
    const auc = entry.metrics?.auc;
    return (
      <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-800">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        Serving production model #{entry.model_id}
        {typeof auc === 'number' ? ` · AUC ${auc.toFixed(3)}` : ''}
      </div>
    );
  }
  if (entry.serving_source === 'synthetic_fallback') {
    const auc = entry.metrics?.auc;
    return (
      <div
        className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
        title="No production model trained yet — synthetic prior is serving until enough real ERAs accumulate."
      >
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
          Serving synthetic prior #{entry.model_id}
          {typeof auc === 'number' ? ` · AUC ${auc.toFixed(3)}` : ''}
        </div>
        <div className="mt-0.5 text-[10px] text-amber-700">
          Cold-start fallback. Auto-replaces when a production model trains
          successfully on real ERAs.
        </div>
      </div>
    );
  }
  return (
    <div className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-zinc-200 bg-zinc-50 px-2 py-1 text-[11px] text-ink-500">
      <span className="h-1.5 w-1.5 rounded-full bg-zinc-400" />
      No model serving — using rule-based scorer
    </div>
  );
}

function Dl({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-ink-400">{label}</dt>
      <dd className="text-ink">{children}</dd>
    </div>
  );
}

function SyntheticPriorsPanel({ summary }: { summary: SyntheticSummary | null }) {
  // Find the latest active (or most recent) synthetic prior per module.
  // priors comes back ordered by trained_at DESC.
  const latestByModule = (() => {
    const out: Record<string, SyntheticPrior> = {};
    for (const p of summary?.priors ?? []) {
      if (!out[p.module]) out[p.module] = p;
    }
    return out;
  })();
  const denial = latestByModule['denial_classifier'];
  const braided = latestByModule['braided_ranker'];
  const synthLines = summary?.counts?.synth_lines ?? 0;
  const synthDenials = summary?.counts?.synth_denial_labels ?? 0;
  const hasData = synthLines > 0;

  function fmtMetric(p: SyntheticPrior | undefined, key: string) {
    const v = p?.metrics?.[key];
    if (v == null) return '—';
    if (typeof v === 'number') return v.toFixed(3);
    return String(v);
  }
  function fmtDate(d: string | null | undefined) {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleString();
    } catch {
      return String(d);
    }
  }

  return (
    <section className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
            <Sparkles className="h-4 w-4 text-indigo-600" />
            Synthetic priors
          </h2>
          <p className="mt-0.5 max-w-3xl text-xs text-ink-500">
            Models trained on CMS DE-SynPUF public data, stored separately
            (<code className="text-[11px]">is_synthetic_prior=TRUE</code>) and used to
            warm-start the weekly production training jobs above. The synthetic data
            never serves inference directly — it just gives the production models a
            better-than-random starting point while real ERAs are still accumulating.
          </p>
        </div>
        <Link
          to="/admin/billing-ml/synthetic-pretrain"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-indigo-300 bg-surface px-2.5 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-100"
        >
          Manage <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {!hasData ? (
        <div className="mt-2 rounded-md border border-dashed border-indigo-200 bg-surface p-3 text-xs text-ink-500">
          No synthetic data ingested yet.{' '}
          <Link to="/admin/billing-ml/synthetic-pretrain" className="text-indigo-700 underline">
            Start an ingest →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="rounded-md border border-v2border-muted bg-paper p-3">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-ink">denial_classifier</span>
              <span className="text-[10px] uppercase tracking-wide text-ink-400">
                {denial ? `model #${denial.id}` : 'none'}
              </span>
            </div>
            <dl className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
              <Dl label="AUC">{fmtMetric(denial, 'auc')}</Dl>
              <Dl label="Brier">{fmtMetric(denial, 'brier')}</Dl>
              <Dl label="Rows">{denial?.training_rows?.toLocaleString() ?? '—'}</Dl>
            </dl>
            <p className="mt-1 text-[10px] text-ink-400">Trained {fmtDate(denial?.trained_at)}</p>
          </div>
          <div className="rounded-md border border-v2border-muted bg-paper p-3">
            <div className="flex items-center justify-between text-xs">
              <span className="font-medium text-ink">braided_ranker</span>
              <span className="text-[10px] uppercase tracking-wide text-ink-400">
                {braided ? `model #${braided.id}` : 'none'}
              </span>
            </div>
            <dl className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
              <Dl label="Activities">{braided?.metrics?.activities?.toLocaleString() ?? '—'}</Dl>
              <Dl label="Pairs">{braided?.metrics?.pairs?.toLocaleString() ?? '—'}</Dl>
              <Dl label="Rows">{braided?.training_rows?.toLocaleString() ?? '—'}</Dl>
            </dl>
            <p className="mt-1 text-[10px] text-ink-400">Trained {fmtDate(braided?.trained_at)}</p>
          </div>
        </div>
      )}

      {hasData && (
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-ink-500">
          <span>
            <strong className="text-ink">{synthLines.toLocaleString()}</strong> synthetic lines
          </span>
          <span>·</span>
          <span>
            <strong className="text-ink">{synthDenials.toLocaleString()}</strong> denial labels
          </span>
          {summary?.latest_drift?.started_at && (
            <>
              <span>·</span>
              <span>last drift check {fmtDate(summary.latest_drift.started_at)}</span>
            </>
          )}
        </div>
      )}
    </section>
  );
}
